<template>
  <UForm
    :schema="schema"
    :state="state"
    class="grid grid-cols-1 gap-4 sm:grid-cols-2"
    @submit="onSubmit"
  >
    <UFormField size="lg" hint="Required" label="First Name" class="col-span-1">
      <UInput v-model="state.firstName" class="w-full" required />
    </UFormField>
    <UFormField size="lg" hint="Required" label="Last Name" class="col-span-1">
      <UInput v-model="state.lastName" class="w-full" required />
    </UFormField>
    <UFormField size="lg" hint="Required" label="Email" class="col-span-1">
      <UInput v-model="state.email" type="email" class="w-full" required />
    </UFormField>
    <UFormField size="lg" hint="Required" label="Phone" class="col-span-1">
      <UInput v-model="state.phone" class="w-full" required />
    </UFormField>
    <UFormField
      v-if="organisationRoute || form.show_organisation"
      size="lg"
      hint="Required"
      label="Organisation"
      class="col-span-1 sm:col-span-2"
    >
      <UInput v-model="state.organisation" class="w-full" required />
    </UFormField>

    <UFormField
      v-if="!form.hide_group_size"
      size="lg"
      hint="Required"
      label="Group Size"
      class="col-span-1 sm:col-span-2"
    >
      <UInput v-model="state.groupSize" class="w-full" type="number" required />
    </UFormField>

    <UFormField
      v-if="!route.path.includes('/groups/') && !form.hide_voucher_code"
      size="lg"
      label="Voucher Code"
      class="col-span-1 sm:col-span-2"
    >
      <UInput v-model="state.voucher" class="w-full" />
    </UFormField>

    <div class="col-span-1 text-sm sm:col-span-2">
      <div>
        <div class="flex content-center items-center justify-between">
          <label class="block font-medium text-[--ui-text]"> Message </label>
          <span class="text-[--ui-text-muted]">Required</span>
        </div>
      </div>
      <div class="relative mt-1">
        <UTextarea
          v-model="state.message"
          placeholder="Enter your message..."
          required
          class="w-full"
        />
      </div>
    </div>

    <UFormField name="other" size="lg" label="Other" class="spandex">
      <UInput v-model="state.other" class="w-full" />
    </UFormField>

    <div class="col-span-1 sm:col-span-2">
      <USwitch
        v-model="state.privacy"
        required
        label="By selecting this, you agree to our privacy policy."
      />
    </div>

    <UButton
      type="submit"
      loading-auto
      :disabled="!state.privacy"
      class="col-span-1 w-full justify-center text-center sm:col-span-2"
      @click="
        // @ts-ignore - gtag is a valid method but type is not properly defined
        proxy.gtag('event', 'booking_form_submitted');
        $clientPosthog?.capture('booking_form_submitted');
      "
    >
      Send Message
    </UButton>
  </UForm>
</template>

<script setup lang="ts">
import { z } from 'zod';
import type { FormSubmitEvent } from '#ui/types';

const { proxy } = useScriptGoogleAnalytics();
const config = useRuntimeConfig(); // Add this to access runtime config
const props = defineProps({
  blok: {
    type: Object,
    default: () => ({}),
  },
  form: {
    type: Object,
    default: () => ({}),
  },
  product: {
    type: Object,
    default: () => ({}),
  },
});
const emits = defineEmits(['submitted']);
const route = useRoute();

const organisationRoute = computed(() => {
  return (
    route.path.includes('/schools') ||
    route.path.includes('/clubs') ||
    route.path.includes('/corporates')
  );
});

const schema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email('Invalid email'),
  phone: z.string(),
  organisation: z.string().optional(),
  groupSize: z.number().optional(),
  voucher: z.string().optional(),
  message: z.string(),
  department: z.string(),
  enquiry: z.string(),
  booking: z.boolean(),
  other: z.string().optional(),
  privacy: z.boolean(),
});

type Schema = z.output<typeof schema>;

const state = reactive<Partial<Schema>>({
  firstName: undefined,
  lastName: undefined,
  email: undefined,
  phone: undefined,
  groupSize: undefined,
  voucher: undefined,
  message: undefined,
  organisation: undefined,
  department: props.form.department,
  enquiry: props.form.enquiry,
  other: undefined,
  booking: true,
  privacy: false,
});

const toast = useToast();

async function onSubmit(event: FormSubmitEvent<Schema>) {
  // Use the environment variable instead of props.form.webhook
  await useFetch(config.public.ZAPIER_CONTACT_FORM, {
    method: 'POST',
    mode: 'no-cors',
    body: event.data,
  });

  state.firstName = undefined;
  state.lastName = undefined;
  state.email = undefined;
  state.phone = undefined;
  state.groupSize = undefined;
  state.voucher = undefined;
  state.message = undefined;
  state.organisation = undefined;
  state.other = undefined;
  state.privacy = false;

  // Close Modal
  emits('submitted', true);

  // Show Success Toast
  toast.add({
    title: 'Success',
    icon: 'i-lucide-circle-check',
    description: 'Your message has been sent.',
    color: 'success',
  });

  // Redirect User If Enabled
  if (props.form.enable_redirect) {
    if (props.form.redirect_link.linktype === 'story') {
      navigateTo(props.form.redirect_link.cached_url);
    } else {
      navigateTo(props.form.redirect_link.url);
    }
  }
}
</script>

<style lang="css" scoped>
.spandex {
  display: none;
}
</style>
